/**
 * game-garden.js — six plots, a watering can, and four flower markets.
 *
 * THIS FILE OWNS FINGERS AND NOTHING ELSE.
 *
 * Every question about what a plot IS — how grown, which stage, whether it is
 * wet, whether it is in season — is answered by the worker and drawn here.
 * The page sends no timestamps and no growth, because there is nothing here
 * for a modified client to claim: growth is settled server-side from the
 * row's own `watered_at` and `tick_at`. See backend/src/garden.js.
 *
 * THE DRAWING IS THE LAB'S, bundled into arcade-garden-art.js from
 * art/garden-lab.mjs, together with the worker's GARDEN config — so the names,
 * the seasons and the market prices on this page are the same objects the
 * worker checks against, not a second list.
 *
 * ONE REDRAW, FROM ONE MODEL. Every action asks the worker, takes the answer
 * as the new truth and repaints from it. There is no optimistic local state to
 * drift out of step — the thing that made the omiyage shelf hard, and the
 * reason a stamp once vanished on a phone.
 *
 * CLICKS ARE DELEGATED ON THE ROOT, which is the one element no repaint ever
 * replaces. Everything inside it is rebuilt, so a listener bound to a button
 * would be a listener bound to a button that no longer exists.
 */
(function () {
  'use strict';

  var Art = window.GardenArt;
  /* THE YARD IS OPTIONAL, and this is the line that makes it optional.
     It rides on the garden art bundle (see art/emit-garden-art.mjs), so if
     that bundle is an older one this page keeps working and draws the six
     cards exactly as it did before, rather than going blank.

     IT USED TO HAVE ITS OWN BUNDLE AND ITS OWN SCRIPT TAG, so the view could
     be switched off by deleting a line from the section. That is what cost
     an afternoon: the assets reached the theme, the section did not, the tag
     never appeared, and this line quietly drew the old page with no error
     anywhere. The fallback was working perfectly and was the reason nothing
     could be diagnosed from the outside. One bundle now, and switching the
     yard off is a change to this file instead. */
  var Yard = window.YardArt || null;
  var root = document.querySelector('[data-garden-game]');
  if (!root || !Art) return;

  var GARDEN = Art.GARDEN;
  /* The station names, for the one sentence that tells her where to go. The
     rally owns these; four words are not worth importing it into this
     bundle, and a wrong one here is a wrong signpost and nothing worse. */
  var CITY_NAME = { tokyo: 'Tokyo', nagoya: 'Nagoya', kyoto: 'Kyoto', osaka: 'Osaka' };
  var BASE = '/apps/arcade';
  var state = null;        // the worker's last word, and the only truth here
  var busy = false;

  async function api(path, body) {
    var res = await fetch(BASE + path, {
      method: body ? 'POST' : 'GET',
      headers: body
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
    });
    var data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (!res.ok) {
      /* THE WORKER'S OWN SENTENCE, when it wrote one. Every refusal in
         garden.js carries a message written for a child ("You have no packet
         of those. The city markets sell them."), and replacing it with
         "Something went wrong" throws away the only part that helps. */
      var err = new Error((data && data.message) || 'That did not work.');
      /* `error`, NOT `code`.
         ------------------------------------------------------------------
         The worker serialises every refusal as { error: <code>, message } —
         see json() and the HttpError catch at the bottom of worker.js — and
         every other game in this arcade reads it as `data.error`. This file
         read `data.code`, which is never set, so err.code was undefined on
         every refusal and any branch on it was dead code. It went unnoticed
         because the MESSAGE was still right; only the code was missing. */
      err.code = (data && data.error) || ('http_' + res.status);
      throw err;
    }
    return data;
  }

  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  };

  /* NEVER SHOW A CHILD A CODE.
     ------------------------------------------------------------------
     worker.js's HttpError does `super(message || code)`, so a refusal thrown
     without a sentence arrives with its message EQUAL to its code. That is
     how a guest tapping Plant was shown the word "login_required" — my own
     error path printing the worker's token verbatim. When message and code
     match there is no human sentence to show, so one is supplied here. */
  var HUMAN = {
    login_required: 'Sign in to start a garden — your plots are kept with your account.',
    not_found: 'The garden is not open yet.',
    server_error: 'Something went wrong at our end. Try again in a moment.',
  };
  function humanise(e) {
    if (e && e.message && e.message !== e.code) return e.message;
    return (e && HUMAN[e.code]) || 'That did not work. Try again in a moment.';
  }

  function say(msg, how) {
    var el = root.querySelector('[data-said]');
    if (!el) return;
    el.textContent = msg || '';
    el.className = 'gd-said' + (how ? ' is-' + how : '');
  }

  /* ---- what a plot should say ---------------------------------------- *
   * THE MOST USEFUL TRUE THING, not a summary. A plot that always says the
   * same sentence is a plot nobody reads. */
  function plotLine(p) {
    /* AN EMPTY BED NOW HAS TWO STATES, and telling her to plant something in
       ground nobody has broken is advice that leads straight to a refusal.
       The line says which of the two it is, so the bubble she should reach
       for is the one the sentence already named. */
    if (!p.plant) {
      return p.tilled
        ? { text: 'Hoed, and ready for a seed', warn: false }
        : { text: 'Empty \u2014 hoe it first', warn: false };
    }
    if (p.ready) return { text: 'Ready to pick', warn: false };
    if (!p.wet) return { text: 'Thirsty — it has stopped growing', warn: true };
    if (p.inSeason === false) {
      /* SAID PLAINLY rather than left to be inferred from a plant that seems
         slow. A child who can read "out of season, growing slowly" has learnt
         what a season is; a child watching an unexplained crawl has learnt
         that the game is broken. */
      return { text: 'Growing slowly — out of season', warn: false };
    }
    return { text: 'Growing', warn: false };
  }

  /* ---- THE BUBBLE MENU ----------------------------------------------- *
   * Barb: "if a plot is clicked, a small menu should open with icon bubbles
   * for hoe, water, plant, harvest."
   *
   * FOUR BUBBLES, ALWAYS THE SAME FOUR, IN THE SAME ORDER — which is the
   * order the garden happens in. A menu that showed only what she can do
   * right now would be a different menu every tap, and she would never learn
   * that hoeing comes before planting; she would just find that the buttons
   * move. So the ones she cannot use are drawn dim, and stay where they were.
   *
   * AND A DIM BUBBLE IS STILL A BUTTON. Tapping one says why, in the worker's
   * own words. A disabled control that does nothing when a child taps it
   * teaches her nothing except that the game ignores her.
   *
   * `live` and `why` below are read off the flags the worker sends with each
   * plot — canTill, canPlant, canWater, canPick — rather than worked out
   * again here, so a bubble drawn live is a bubble whose endpoint will agree.
   */
  var BUBS = [
    { act: 'till', name: 'Hoe',
      live: function (p) { return !!p.canTill; },
      why: function (p) {
        if (p.plant) return 'Something is already growing there.';
        if (p.tilled) return 'That bed is ready already \u2014 put a seed in it.';
        return 'You need a hoe to break the ground. The city markets sell one.';
      } },
    { act: 'plant', name: 'Plant',
      live: function (p) { return !!p.canPlant; },
      why: function (p) {
        if (p.plant) return 'Something is already growing there.';
        return 'Hoe the bed first \u2014 a seed will not go into ground nobody has broken.';
      } },
    { act: 'water', name: 'Water',
      live: function (p) { return !!p.canWater; },
      why: function (p) {
        if (!p.plant) return 'There is nothing planted there yet.';
        return 'You need a watering can. The city markets sell one.';
      } },
    { act: 'pick', name: 'Harvest',
      live: function (p) { return !!p.canPick; },
      why: function (p) {
        if (!p.plant) return 'There is nothing planted there yet.';
        return 'Not ready yet \u2014 keep it watered and come back.';
      } },
  ];

  /* ---- THE YARD ------------------------------------------------------- *
   * Barb: "it is supposed to be an isometric space my character can walk
   * around in... just outside the house with a mailbox in front of the
   * house."
   *
   * IT IS THE PICTURE, AND THE CARDS ARE STILL THE CONTROLS — for now, and
   * deliberately. Tapping a bed out here does not open a second menu of its
   * own; it opens THAT BED'S menu in the list below and scrolls to it. Two
   * ways to reach one menu is a shortcut. Two menus that both plant things
   * is two things to keep in step, and they would not stay in step.
   *
   * She cannot walk in it yet. That is the next piece, and until it is here
   * the yard is a view of her garden rather than a place she is standing in.
   */
  /* WHERE A BED SITS ON THE PICTURE, as a percentage of it.
     Percentages rather than pixels because the drawing is scaled to whatever
     width the panel is — a popup placed at "x = 214px" is in the right place
     on exactly one screen. */
  function bedSpot(slot) {
    var at = Yard.PLOT_AT[slot];
    var g = Yard.geometry();
    var c = Yard.tileToScreen(at.u + 0.5, at.v + 0.5, 5);
    return { x: (c[0] / g.w) * 100, y: (c[1] / g.h) * 100 };
  }

  /* ---- THE POPUP ------------------------------------------------------ *
   * Barb: "I don't like that the cards are underneath the game. It should be
   * an in game pop up when the growing plot is clicked."
   *
   * SO THE CARDS ARE GONE. They were a second drawing of the same six beds,
   * sitting under the yard, and everything they said is said here instead —
   * over the bed it is about, which is the whole difference between reading
   * about a plot and working on one.
   *
   * IT IS ANCHORED ABOVE THE BED AND CLAMPED TO THE PICTURE. A popup over
   * bed 5 would otherwise hang off the left edge of a phone; `left` is held
   * between 26% and 74% so the box stays on screen while its tail still
   * points at the right bed.
   */
  function popHtml(p) {
    var def = p.plant ? GARDEN.plants[p.plant] : null;
    var line = plotLine(p);
    var pct = Math.max(0, Math.min(100, Math.round((p.growth / GARDEN.max) * 100)));
    var at = bedSpot(p.slot);
    /* CLAMPED BY THE BOX'S OWN HALF-WIDTH. The popup is 210px in a yard that
       is about 376px across on a phone, so its half-width is roughly 28% and
       anything closer than that to an edge hangs off it — which is what bed
       2, at 30.4%, was doing. 31 leaves a margin; every one of the six beds
       sits between 30.4% and 62.6%, so in practice nothing is moved more
       than a couple of pixels and the tail still points true. */
    var left = Math.max(32, Math.min(68, at.x));

    /* IT ALWAYS OPENS UPWARD, and there is no branch for the other case.
       The first version had one — "open downward if the bed is near the
       top" — and the threshold was a guess that no bed ever crossed, so the
       branch was dead and the beds that DID overflow opened upward anyway
       and ran off the roof. The real fix was the box, not the branch: four
       bubbles across instead of two-by-two makes it short enough that every
       one of the six fits above its own bed. The harness opens all six and
       measures, so a seventh bed placed too near the house fails there
       rather than in front of a child. */
    return '<div class="gd-pop'
      + (p.ready ? ' is-ready' : '') + '" data-pop data-slot="' + p.slot + '"'
      + ' role="dialog"'
      + ' aria-label="' + esc(def ? def.name : 'An empty bed') + '"'
      + ' style="left:' + left.toFixed(1) + '%; top:' + at.y.toFixed(1) + '%">'
      + '<button class="gd-pop__x" data-act="shut" aria-label="Close">&times;</button>'
      + '<p class="gd-pop__name">' + (def ? esc(def.name) : 'Empty bed')
      + (def ? ' <span class="gd-plot__jp">' + esc(def.jp) + '</span>' : '') + '</p>'
      + (p.plant
          ? '<span class="gd-bar-grow"><i style="width:' + pct + '%"></i></span>'
          : '')
      + '<p class="gd-pop__say' + (line.warn ? ' is-warn' : '') + '">'
      + esc(line.text) + '</p>'
      + bubsHtml(p)
      + (choosing === p.slot
          ? '<div class="gd-pick"><p class="gd-pick__q">What shall go in?</p>'
            + pickerHtml(p.slot) + '</div>'
          : '')
      + '</div>';
  }

  /* THE KEYBOARD'S WAY IN. The yard is one <svg role="img"> — a picture,
     correctly — so it is not reachable by tab, and the cards that used to be
     the keyboard path are gone. These six buttons are the path: off-screen
     for a mouse, first-class for a keyboard and a screen reader, and each one
     says what its bed is doing rather than "Plot 3". */
  function keysHtml() {
    if (state.guest) return '';
    return '<ul class="gd-keys">' + (state.plots || []).map(function (p) {
      var def = p.plant ? GARDEN.plants[p.plant] : null;
      return '<li><button data-act="open" data-slot="' + p.slot + '"'
        + ' aria-expanded="' + (open_ === p.slot ? 'true' : 'false') + '">'
        + 'Bed ' + (p.slot + 1) + ': ' + (def ? esc(def.name) + ', ' : '')
        + esc(plotLine(p).text) + '</button></li>';
    }).join('') + '</ul>';
  }

  /* HER, STANDING BY HER OWN DOOR.
     ------------------------------------------------------------------
     yard-lab has taken an avatar since the day it was written and nobody
     ever handed it one, so the garden has been a yard with nobody in it.

     THE FEET GO ON THE TILE. billboard() translates to the tile's ground
     point, so the drawing has to be positioned with its feet at the origin
     — half its width left and nearly all its height up. Avatar.full draws
     her standing in her own box, so that is width/2 and height.

     SHE IS NOT WALKING YET. She stands just outside the door, on the path,
     which is where somebody who has come out to look at the garden would
     be. Moving her is the next piece. */
  /* WHERE SHE STARTS: just outside her own door, on the path. After that
     `here` is wherever she has walked to. */
  var STAND = { u: 2.5, v: 1.5 };
  /* HER WIDTH. The drawing is 340x420, so the height that comes back is
     about 1.235 times this — which is what the offset below has to use, not
     the width, or she is planted a fifth of her own height into the lawn. */
  var HER_W = 54;
  var HER_H = Math.round(HER_W * 420 / 340);

  function herArt() {
    if (!look || !window.Avatar || !window.Avatar.full) return null;
    var svg;
    /* `bg: null` AND `sparkle: false`. Avatar.full is the dressing-room
       portrait: it paints a pink card behind her and scatters white
       sparkles on it, which is right in the wardrobe and is a pink
       rectangle stuck to the lawn out here. Both are options; without them
       she arrived standing in a card. */
    try { svg = window.Avatar.full(look, HER_W, { bg: null, sparkle: false }); }
    catch (e) { return null; }
    if (!svg) return null;
    return {
      u: here.u, v: here.v,
      /* FEET ON THE TILE. billboard() translates to the tile's ground point,
         so she is shifted half her width left and all her height up, less a
         few units so she stands in the grass rather than on top of it. */
      svg: '<g data-her transform="translate(' + (-HER_W / 2) + ' '
        + (-HER_H + 5) + ')">' + svg + '</g>',
    };
  }

  /* ---- THE DOOR AND THE POSTBOX --------------------------------------- *
   * Barb: "when clicking the mailbox a small in game window with
   * notifications should show. when clicking the door, a small window with
   * the option to go inside appears. when clicked, it takes you to the room
   * designer game."
   *
   * THE SAME BOX THE BEDS USE, so there is one popup in this game and not
   * three. It is placed by the same measuring pass, anchored to the thing it
   * is about, and closed the same way.
   */
  var ROOM_URL = '/pages/arcade-room';

  /* WHAT THE POSTBOX HAS IN IT.
     ------------------------------------------------------------------
     NO NEW ENDPOINT AND NO DEPLOY. /session already carries the same inbox
     the arcade's bell reads — emotes, treats, dares, praise, keepsakes, who
     watered her garden, and who has asked to be her friend — and this page
     is already asking for it to find out what she looks like. A second door
     for the same two lists would be a second thing to keep in step. */
  function inboxLines() {
    var box = (session && session.inbox) || {};
    var who = function (x) { return esc(x.handle || x.from || 'A friend'); };
    var out = [];
    (box.waterings || []).forEach(function (x) {
      out.push(who(x) + ' watered your ' + esc((x.name || 'plant').toLowerCase()) + '.');
    });
    (box.requests || []).forEach(function (x) {
      out.push(who(x) + ' would like to be friends.');
    });
    (box.keepsakes || []).forEach(function (x) {
      out.push(who(x) + ' brought you something back.');
    });
    (box.treats || []).forEach(function (x) { out.push(who(x) + ' sent you a treat.'); });
    (box.praise || []).forEach(function (x) { out.push(who(x) + ' liked something you made.'); });
    (box.emotes || []).forEach(function (x) { out.push(who(x) + ' waved at you.'); });
    (box.dares || []).forEach(function (x) { out.push(who(x) + ' dared you.'); });
    return out;
  }

  function thingHtml() {
    if (!atThing) return '';
    var boxes = Yard.hitBoxes();
    var b = boxes[atThing];
    var mid = (b.x1 + b.x2) / 2, top = b.y1;
    var g = Yard.geometry();
    var body, title;

    if (atThing === 'door') {
      title = 'The front door';
      body = '<p class="gd-pop__say">Your room is through here.</p>'
        + '<a class="gd-go" href="' + ROOM_URL + '">Go inside</a>';
    } else {
      title = 'The postbox';
      var lines = inboxLines();
      body = lines.length
        ? '<ul class="gd-notes">' + lines.map(function (t) {
            return '<li>' + t + '</li>';
          }).join('') + '</ul>'
        /* AN EMPTY POSTBOX SAYS SO, and says what would put something in it.
           "No notifications" is a true sentence that teaches nothing. */
        : '<p class="gd-pop__say">Nothing in the post today. Friends watering '
          + 'your garden and presents from their trips turn up here.</p>';
    }

    return '<div class="gd-pop gd-pop--thing" data-pop data-thing="' + atThing + '"'
      + ' role="dialog" aria-label="' + esc(title) + '"'
      + ' style="left:' + ((mid / g.w) * 100).toFixed(1) + '%;'
      + ' top:' + ((top / g.h) * 100).toFixed(1) + '%">'
      + '<button class="gd-pop__x" data-act="shut" aria-label="Close">&times;</button>'
      + '<p class="gd-pop__name">' + esc(title) + '</p>'
      + body + '</div>';
  }

  function yardHtml() {
    if (!Yard) return '';
    var svg = Yard.render({ plots: state.plots || [], her: herArt(),
      paint: state.paint || {} }, { label: 'Your garden, outside the house' });
    var p = open_ === null ? null : (state.plots || [])[open_];
    /* SAY IT IS TAPPABLE. Nothing about a picture announces that you can
       press it, and a shortcut nobody knows about is not a shortcut. The
       line also admits what this view cannot do yet, rather than leaving her
       to work out why her character is not in it. */
    /* THE BAG SITS IN THE BOTTOM RIGHT OF THE GAME, over the pavement, where
       a bag you are carrying belongs — not up in the title bar with the
       season. A guest has no bag: there is nothing in it and nothing to put
       in. */
    var LABEL = { paint: 'Paint your house', markets: 'Flower markets',
      friends: "Friends' gardens", bag: 'Your backpack' };
    var bag = state.guest ? ''
      : '<div class="gd-tools-corner">' + (Art.PANELS || ['bag']).map(function (k) {
          return '<button class="gd-pack' + (panel === k ? ' is-on' : '') + '"'
            + ' data-act="panel" data-panel="' + k + '"'
            + ' aria-expanded="' + (panel === k ? 'true' : 'false') + '"'
            + ' aria-label="' + esc(LABEL[k] || k) + '">'
            + (Art.panelSvg ? Art.panelSvg(k, { label: '' }) : Art.packSvg({ label: '' }))
            + '</button>';
        }).join('') + '</div>';

    return '<div class="gd-yard" data-yard>' + svg + bag
      + (p && !state.guest ? popHtml(p) : '')
      + (state.guest ? '' : thingHtml())
      + (panel && !state.guest ? panelHtml() : '') + '</div>'
      + keysHtml()
      /* IT IS NOT COMING NEXT, IT IS HERE. The line used to promise walking
         about as something still to come, and it has been in the game since
         she could be tapped across the yard. A promise left up after it has
         been kept reads as a game that has not been touched since. */
      + '<p class="gd-yard__say">Tap a bed to work on it.</p>';
  }

  function bubsHtml(p) {
    return '<div class="gd-bubs" role="group" aria-label="What to do with plot '
      + (p.slot + 1) + '">' + BUBS.map(function (b) {
        var live = b.live(p);
        /* WET IS NOT DIM. Watering an already-damp plot is allowed and free —
           the worker says so — so the bubble stays live and wears a tick
           instead, which answers "have I done this one?" without refusing. */
        var done = live && b.act === 'water' && p.wet;
        var label = b.name + (done ? ', watered' : live ? '' : ', not yet');
        return '<button class="gd-bub' + (live ? '' : ' is-dim')
          + (done ? ' is-done' : '') + '"'
          + ' data-act="' + (live ? b.act : 'why') + '"'
          + ' data-slot="' + p.slot + '"'
          + (live ? '' : ' data-why="' + esc(b.why(p)) + '"')
          + ' aria-label="' + esc(label) + '">'
          + Art.actionSvg(b.act, { label: '' })
          + '<span class="gd-bub__name">' + esc(b.name) + '</span></button>';
      }).join('') + '</div>';
  }

  function plotHtml(p) {
    var def = p.plant ? GARDEN.plants[p.plant] : null;
    var line = plotLine(p);
    var pct = Math.max(0, Math.min(100, Math.round((p.growth / GARDEN.max) * 100)));
    var open = open_ === p.slot;
    var cls = 'gd-plot'
      + (p.ready ? ' is-ready' : '')
      + (p.plant && !p.wet ? ' is-dry' : '')
      + (open ? ' is-open' : '');

    var label = def ? def.name + ', ' + line.text : 'an empty plot';
    var art = Art.plotSvg(p.plant, p.stage || 'seeded', { label: label });
    var face = art
      + '<span class="gd-plot__name">' + (def ? esc(def.name) : 'Empty')
      + (def ? ' <span class="gd-plot__jp">' + esc(def.jp) + '</span>' : '') + '</span>'
      + (p.plant
          ? '<span class="gd-bar-grow"><i style="width:' + pct + '%"></i></span>'
          : '')
      + '<span class="gd-plot__say' + (line.warn ? ' is-warn' : '') + '">'
      + esc(line.text) + '</span>';

    /* A GUEST SEES THE GARDEN AND NOT THE BUTTONS. She can read the plants,
       the seasons and the market prices — which is the advertisement — and
       is asked to sign in once, at the top, rather than six times in a row
       by six buttons that cannot work. */
    if (state.guest) return '<li class="' + cls + '">' + face + '</li>';

    return '<li class="' + cls + '">'
      + '<button class="gd-plot__hit" data-act="open" data-slot="' + p.slot + '"'
      + ' aria-expanded="' + (open ? 'true' : 'false') + '">' + face + '</button>'
      + (open ? bubsHtml(p) : '')
      + (open && choosing === p.slot
          ? '<div class="gd-pick"><p class="gd-pick__q">What shall go in?</p>'
            + pickerHtml(p.slot) + '</div>'
          : '')
      + '</li>';
  }

  /* ---- THE BACKPACK --------------------------------------------------- *
   * Barb: "add a backpack icon where they can access their seeds and
   * gardening tools."
   *
   * ONE PLACE FOR THE THINGS SHE OWNS. The basket used to be a panel of its
   * own halfway down the page and the tools had nowhere at all; a child
   * looking for "have I got a hoe?" had to read the whole page to find out.
   * Now the shed, the seed packets and the basket are the three shelves of
   * one bag, opened from one icon in the bar.
   *
   * A TOOL SHE HAS NOT GOT IS SHOWN, NOT HIDDEN, with its price on it. That
   * is the only way the hoe rule explains itself: a child who taps a dim
   * Hoe bubble is told she needs a hoe, opens the bag, and sees what one
   * costs and that the markets sell it. Hiding it would leave the refusal
   * with nowhere to lead.
   */
  function shedHtml() {
    var tools = state.tools || {};
    var shop = state.toolShop || {};
    var keys = Object.keys(shop);
    if (!keys.length) return '';
    return '<ul class="gd-tools">' + keys.map(function (k) {
      var t = shop[k];
      var got = !!tools[k];
      return '<li class="gd-tool' + (got ? ' is-got' : '') + '">'
        + Art.toolSvg(k, { label: t.name })
        + '<span class="gd-tool__name">' + esc(t.name)
        + ' <span class="gd-plot__jp">' + esc(t.jp || '') + '</span></span>'
        + '<span class="gd-tool__say">'
        + (got ? 'in your bag' : esc(t.cost) + ' points at any market')
        + '</span></li>';
    }).join('') + '</ul>';
  }

  /* ---- PAINTING THE HOUSE --------------------------------------------- *
   * Barb: "let users change the color of house, roof, and door... Give
   * pastel colors to choose from and a color picker like in the dress up
   * game."
   *
   * SWATCHES AND SLIDERS, the dressing room's arrangement. Ten pastels are
   * the quick answer; the three sliders are for a child who wants a colour
   * nobody put in the tin. `<input type="color">` would have been shorter
   * and opens the operating system's grey dialog in the middle of a game
   * drawn in pink — the dressing room made that call already and this
   * follows it.
   *
   * THE PREVIEW IS THE HOUSE, rendered through the same function that draws
   * it in the yard. The shading is derived from the colour she picks, so a
   * flat swatch would be telling her the truth about a quarter of what she
   * is going to see.
   */
  var part = 'house';          // which of the three she is painting

  /* HSL <-> HEX, for the sliders. Small enough to keep here and no business
     in the art: the lab is asked for colours, not for arithmetic about
     them. */
  function hex2hsl(hex) {
    var r = parseInt(hex.slice(1, 3), 16) / 255;
    var g = parseInt(hex.slice(3, 5), 16) / 255;
    var b = parseInt(hex.slice(5, 7), 16) / 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
    var l = (mx + mn) / 2, h = 0, sat = 0;
    if (d) {
      sat = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
      h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
      h *= 60;
    }
    return [Math.round(h), Math.round(sat * 100), Math.round(l * 100)];
  }
  function hsl2hex(h, sat, l) {
    var S = sat / 100, L = l / 100;
    var k = function (n) { return (n + h / 30) % 12; };
    var a = S * Math.min(L, 1 - L);
    var f = function (n) {
      var v = L - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
      return Math.round(v * 255).toString(16).padStart(2, '0');
    };
    return '#' + f(0) + f(8) + f(4);
  }

  function partColour(key) {
    var mine = (state.paint || {})[key];
    var shop = (state.paintShop && state.paintShop.parts) || {};
    return (mine || (shop[key] && shop[key].fallback) || '#ffffff').toLowerCase();
  }

  function paintHtml() {
    var shop = state.paintShop || { parts: {}, palette: [] };
    var partKeys = Object.keys(shop.parts);
    if (!partKeys.length) {
      return '<p class="gd-empty">The paint tin has not arrived yet.</p>';
    }
    var now = partColour(part);
    var hsl = hex2hsl(now);

    var tabs = '<div class="gd-parts">' + partKeys.map(function (k) {
      return '<button class="gd-part' + (part === k ? ' is-on' : '') + '"'
        + ' data-act="part" data-part="' + k + '">'
        + '<i style="background:' + esc(partColour(k)) + '"></i>'
        + esc(shop.parts[k].name) + '</button>';
    }).join('') + '</div>';

    var swatches = '<div class="gd-swatches">' + (shop.palette || []).map(function (c) {
      var on = c.hex.toLowerCase() === now;
      return '<button class="gd-swatch' + (on ? ' is-on' : '') + '"'
        + ' data-act="paint" data-hex="' + esc(c.hex) + '"'
        + ' style="background:' + esc(c.hex) + '"'
        + ' aria-label="' + esc(c.name) + '"'
        + (on ? ' aria-current="true"' : '') + '></button>';
    }).join('') + '</div>';

    /* THE SLIDERS ARE REAL `<input type="range">`: draggable, tappable,
       arrow-key-steppable and labelled, because the browser already did all
       of that and a hand-drawn wheel never gets the keyboard half. */
    var slider = function (kind, label, value, max) {
      return '<label class="gd-slider"><span>' + label + '</span>'
        + '<input type="range" min="0" max="' + max + '" value="' + value + '"'
        + ' data-mix="' + kind + '" aria-label="' + label + '"></label>';
    };
    var mix = '<div class="gd-mix" data-mix-group>'
      + '<p class="gd-sub">Mix your own</p>'
      + slider('h', 'Colour', hsl[0], 359)
      + slider('s', 'Softness', hsl[1], 100)
      + slider('l', 'Lightness', hsl[2], 100)
      + '</div>';

    /* THE PICTURE IS OF THE PART BEING PAINTED. The postbox stands on the
       pavement and the fence runs round the edge of the lawn; neither is in a
       picture of the house, so painting one while the house was on screen was
       a change you could not watch happen.
       WHICH PICTURE IS THE ART'S QUESTION, not this page's. Naming the parts
       here was a second list to keep in step with garden-config, and it went
       stale the first time a part was added. */
    var chip = Yard && Yard.partChip ? Yard.partChip(part, state.paint || {})
      : (Yard && Yard.houseChip ? Yard.houseChip(state.paint || {}) : '');

    return '<div class="gd-paint">'
      + '<div class="gd-paint__pic" data-paint-pic>' + chip + '</div>'
      + tabs + swatches + mix + '</div>';
  }

  function panelHtml() {
    if (panel === 'paint') {
      return '<div class="gd-pop gd-pop--bag" data-pop data-bag data-panel="paint"'
        + ' role="dialog" aria-label="Paint your house">'
        + '<button class="gd-pop__x" data-act="panel" data-panel="paint"'
        + ' aria-label="Close">&times;</button>'
        + '<p class="gd-pop__name">Paint your house</p>'
        + paintHtml() + '</div>';
    }
    if (panel === 'friends') {
      return '<div class="gd-pop gd-pop--bag" data-pop data-bag data-panel="friends"'
        + ' role="dialog" aria-label="Friends\u2019 gardens">'
        + '<button class="gd-pop__x" data-act="panel" data-panel="friends"'
        + ' aria-label="Close">&times;</button>'
        + '<p class="gd-pop__name">' + (visiting ? 'In a friend\u2019s garden'
            : 'Friends\u2019 gardens') + '</p>'
        + (visiting ? visitingHtml() : friendsHtml())
        + '</div>';
    }
    if (panel === 'markets') {
      return '<div class="gd-pop gd-pop--bag" data-pop data-bag data-panel="markets"'
        + ' role="dialog" aria-label="Flower markets">'
        + '<button class="gd-pop__x" data-act="panel" data-panel="markets"'
        + ' aria-label="Close">&times;</button>'
        + '<p class="gd-pop__name">Flower markets</p>'
        + marketsHtml() + '</div>';
    }
    return packHtml();
  }

  function packHtml() {
    /* IN THE GAME, NOT UNDER IT. Barb: "when it is clicked, it is opening up
       a menu underneath the game. It needs to open the menu inside the game
       in a pop up." It is the same box the beds and the door use — one popup
       in this garden, three things that open it — anchored to the bag in the
       corner it was pressed in, and placed by the same measuring pass. */
    return '<div class="gd-pop gd-pop--bag" data-pop data-bag data-panel="bag"'
      + ' role="dialog" aria-label="Your backpack">'
      + '<button class="gd-pop__x" data-act="panel" data-panel="bag"'
      + ' aria-label="Close">&times;</button>'
      + '<p class="gd-pop__name">Your backpack</p>'
      + '<h4 class="gd-sub">Tools</h4>' + shedHtml()
      /* `data-basket` IS A TEST HOOK AND SAYS SO. The harness used to find the
         basket as "the first .gd-sect", which was true until a Friends'
         gardens section was inserted above it — and then the assertion was
         quietly reading the wrong panel. A named hook cannot be moved out
         from under a test by a layout change. */
      + '<h4 class="gd-sub">Seeds and basket</h4>'
      + '<div data-basket>' + basketHtml() + '</div>'
      + '</div>';
  }

  /* Her seed packets and what she has picked. The worker hands these back as
     'plant:kind' keys with counts. */
  function basketHtml() {
    var keys = Object.keys(state.stock || {});
    if (!keys.length) {
      return '<p class="gd-empty">Nothing in the basket yet. Seeds come from the '
        + 'flower markets in the cities.</p>';
    }
    var KIND = { seed: 'seeds', flower: 'flowers', crop: 'picked' };
    /* A PICTURE ON EVERY ROW — Barb: "can you add pictures to the names?"
       Seven white chips told apart only by the words on them is a list you
       read rather than a bag you look in. The picture answers WHICH PLANT
       and the coloured word answers which form of it, which is the split
       that lets her find her strawberry seeds at a glance.

       It is the plot's own drawing of that plant, cropped, not a second set
       of little ones — see chipSvg in garden-lab.mjs. */
    return '<ul class="gd-chips">' + keys.sort().map(function (k) {
      var bits = k.split(':');
      var def = GARDEN.plants[bits[0]];
      if (!def) return '';
      var pic = Art.chipSvg ? Art.chipSvg(bits[0], { label: '' }) : '';
      return '<li class="gd-chip' + (bits[1] === 'flower' ? ' gd-chip--flower' : '') + '">'
        + pic + '<span class="gd-chip__text">' + esc(def.name) + ' <b>'
        + esc(KIND[bits[1]] || bits[1])
        + ' &times;' + (state.stock[k] | 0) + '</b></span></li>';
    }).join('') + '</ul>';
  }

  function marketsHtml() {
    var been = state.visited || [];
    return Object.keys(GARDEN.markets).map(function (city) {
      var m = GARDEN.markets[city];
      /* A STALL SHE HAS NOT BEEN TO IS SHOWN AND SHUT, not hidden and not
         silently broken. Hiding it would hide the reason to ride the rally;
         leaving the buttons live would mean four buttons whose only outcome
         is a refusal. She can see what Kyoto sells and what it would cost —
         that is the advertisement — and one line saying how to get in. */
      var open = been.indexOf(city) > -1;
      var buys = !open ? '' : m.sells.map(function (key) {
        var def = GARDEN.plants[key];
        var out = '<button class="gd-buy" data-act="buy" data-city="' + city
          + '" data-plant="' + key + '" data-kind="seed">'
          + esc(def.name) + ' seeds · ' + def.seed + '</button>';
        /* A CUT FLOWER ONLY WHERE THERE IS ONE. Nobody sells a daikon cut for
           an arrangement, and a button that exists only to be refused is worse
           than no button. */
        if (def.flower) {
          out += '<button class="gd-buy gd-buy--flower" data-act="buy" data-city="' + city
            + '" data-plant="' + key + '" data-kind="flower">'
            + esc(def.name) + ' flowers · ' + def.flower + '</button>';
        }
        return out;
      }).join('');
      /* AND THE SHED, AT EVERY OPEN STALL. Both tools are sold everywhere —
         a hoe is not a seasonal flower, and making it a Kyoto exclusive
         would strand the child who rode to Osaka first. One she already owns
         is dropped rather than shown greyed: there is nothing to buy, the
         backpack already says she has it, and a permanent dead button in
         four markets is four dead buttons. */
      var tools = !open ? '' : Object.keys(state.toolShop || {})
        .filter(function (k) { return !(state.tools || {})[k]; })
        .map(function (k) {
          var t = state.toolShop[k];
          return '<button class="gd-buy gd-buy--tool" data-act="buy" data-city="' + city
            + '" data-plant="' + k + '" data-kind="tool">'
            + esc(t.name) + ' · ' + t.cost + '</button>';
        }).join('');
      var shut = m.sells.map(function (key) {
        var def = GARDEN.plants[key];
        return '<li class="gd-chip">' + esc(def.name) + ' <b>' + def.seed + '</b></li>';
      }).join('');
      return '<div class="gd-market' + (open ? '' : ' is-shut') + '">'
        + '<div class="gd-market__top">'
        + '<b>' + esc(m.name) + '</b>'
        + '<span class="gd-market__jp">' + esc(m.jp) + '</span></div>'
        + (open
            ? '<div class="gd-buys">' + buys + tools + '</div>'
            : '<p class="gd-note" style="margin:6px 0 0">Ride the rally to '
              + esc(CITY_NAME[city] || city) + ' and press the stamp, and this '
              + 'market will sell to you.</p>'
              + '<ul class="gd-chips" style="margin-top:8px">' + shut + '</ul>')
        + '</div>';
    }).join('');
  }

  /* The seed picker, shown only once she has tapped an empty plot. */
  function pickerHtml(slot) {
    var mine = Object.keys(state.stock || {}).filter(function (k) {
      return /:seed$/.test(k) && state.stock[k] > 0;
    });
    if (!mine.length) {
      return '<p class="gd-empty">You have no seed packets. Buy some at a flower '
        + 'market below, then come back.</p>';
    }
    return '<div class="gd-seeds">' + mine.map(function (k) {
      var key = k.split(':')[0];
      var def = GARDEN.plants[key];
      var off = def.season !== state.season;
      return '<button class="gd-buy" data-act="plant" data-slot="' + slot
        + '" data-plant="' + key + '">' + esc(def.name)
        /* OUT OF SEASON IS MARKED, NOT HIDDEN. She may plant it; it will just
           take about twice as long, and she should know that before she does
           rather than wonder afterwards. */
        + (off ? ' · out of season' : '') + '</button>';
    }).join('') + '</div>';
  }

  var choosing = null;     // which slot the picker is open for, or null
  var open_ = null;        // which plot's bubble menu is open, or null
  /* WHICH CORNER WINDOW IS OPEN: 'bag', 'friends', 'markets', or none.
     One variable rather than three flags, because only one of them can be
     open — three booleans is three ways to have two windows at once. */
  var panel = null;
  var atThing = null;      // 'door' or 'mailbox' when one of them is open
  var look = null;         // her saved avatar, once /session has answered
  var session = null;      // and the rest of it — the postbox reads the inbox
  var friends = null;      // her friends, once asked for
  var visiting = null;     // { id, handle, season, plots } of the garden she is in

  /* ---- HER FRIENDS' GARDENS ------------------------------------------ *
   * Barb: "friends can interact with each other's gardens." The watering was
   * built, tested and completely unreachable for a fortnight, because there
   * was no way to SEE whose plot was dry. An endpoint with no door is a
   * feature nobody has.
   *
   * The friends list comes from /friends, which every other page in this
   * arcade already uses, rather than a second list of names kept here. */
  function friendsHtml() {
    if (!friends) return '<p class="gd-empty">Looking for your friends…</p>';
    if (!friends.length) {
      return '<p class="gd-empty">No friends yet. Add one in the friends panel '
        + 'and you can water each other\'s gardens.</p>';
    }
    return '<ul class="gd-chips">' + friends.map(function (f) {
      return '<li><button class="gd-buy" data-act="visit" data-id="' + esc(f.id) + '">'
        + esc(f.handle) + '</button></li>';
    }).join('') + '</ul>';
  }

  function visitingHtml() {
    var v = visiting;
    var dry = v.plots.filter(function (p) { return p.needsWater; }).length;
    var head = '<div class="gd-market__top"><b>' + esc(v.handle) + '&rsquo;s garden</b>'
      + '<span class="gd-market__jp">' + esc(v.season) + '</span></div>';
    var note = dry
      ? (dry === 1 ? 'One plot is thirsty.' : dry + ' plots are thirsty.')
      : 'Everything here is watered.';
    var plots = v.plots.map(function (p) {
      var def = p.plant ? GARDEN.plants[p.plant] : null;
      var label = def ? def.name + (p.ready ? ', ready to pick' : '') : 'an empty plot';
      /* A VISITOR WATERS AND DOES NOTHING ELSE. There is no Pick button here
         and no endpoint behind one: a friend can make a garden grow and can
         take nothing out of it. */
      return '<li class="gd-plot' + (p.needsWater ? ' is-dry' : '') + '">'
        + Art.plotSvg(p.plant, p.stage || 'seeded', { label: label })
        + '<span class="gd-plot__name">' + (def ? esc(def.name) : 'Empty') + '</span>'
        + (p.needsWater
            ? '<button class="gd-do is-water" data-act="help" data-id="' + esc(v.id)
              + '" data-slot="' + p.slot + '">Water it</button>'
            : '<span class="gd-plot__say">'
              + (p.plant ? (p.ready ? 'Ready to pick' : 'Watered') : 'Empty') + '</span>')
        + '</li>';
    }).join('');
    return '<div class="gd-market">' + head
      + '<p class="gd-note" style="margin-top:6px">' + esc(note) + '</p>'
      + '<ul class="gd-plots" style="margin-top:10px">' + plots + '</ul>'
      + '<div class="gd-buys"><button class="gd-buy" data-act="leave">'
      + 'Back to my garden</button></div></div>';
  }

  /* KEEP THE POPUP INSIDE THE PICTURE, MEASURED.
     ------------------------------------------------------------------
     The CSS anchors the box above its bed and that is right for the box on
     its own. It is NOT right once the seed picker opens inside it: four
     seed buttons make it three times taller, `translate(-100%)` lifts it by
     its own new height, and on a desktop it climbed out of the yard
     altogether and sat over the Back to the arcade button.

     The harness had measured all six popups and passed, because it measured
     them CLOSED. A popup that fits when it is small says nothing about the
     one the child actually opens.

     So the anchor is a starting point and this is the clamp: measured, in
     pixels, after the box exists. When it has to move, the tail is dropped —
     a tail that points at the wrong bed is worse than none. */
  function placePop() {
    var pop = root.querySelector('[data-pop]');
    var yard = root.querySelector('.yd-art');
    if (!pop || !yard) return;
    var yb = yard.getBoundingClientRect();
    var pb = pop.getBoundingClientRect();
    if (!yb.height || !pb.height) return;

    /* THE ANCHOR IS WHATEVER THE BOX IS ABOUT — a bed's soil, or the door
       and the postbox, which are not tiles and carry their own boxes. */
    var thing = pop.getAttribute('data-thing');
    var at;
    if (pop.hasAttribute('data-bag')) {
      /* THE BAG'S WINDOW OPENS FROM THE BAG, in the corner it lives in, so
         the clamp below parks it in that corner rather than over the house. */
      at = { x: 100, y: 100 };
    } else if (thing) {
      var g = Yard.geometry(), hb = Yard.hitBoxes()[thing];
      at = { x: ((hb.x1 + hb.x2) / 2 / g.w) * 100, y: (hb.y1 / g.h) * 100 };
    } else {
      at = bedSpot(Number(pop.getAttribute('data-slot')));
    }
    var GAP = 8, EDGE = 6;

    var wantTop = (at.y / 100) * yb.height - pb.height - GAP;
    var maxTop = yb.height - pb.height - EDGE;
    var top = Math.max(EDGE, Math.min(maxTop, wantTop));

    var wantLeft = (at.x / 100) * yb.width - pb.width / 2;
    var left = Math.max(EDGE, Math.min(yb.width - pb.width - EDGE, wantLeft));

    pop.style.top = top.toFixed(1) + 'px';
    pop.style.left = left.toFixed(1) + 'px';
    /* The CSS centres it on its anchor; the numbers above are already the
       box's own corner, so the centring has to come off. */
    pop.style.transform = 'none';
    /* THE TAIL ONLY WHEN IT IS TELLING THE TRUTH — that is, when the box
       ended up where it asked to be, directly over its own bed. */
    var honest = Math.abs(top - wantTop) < 1 && Math.abs(left - wantLeft) < 1;
    pop.classList.toggle('is-floating', !honest);
  }

  function draw() {
    var plots = (state.plots || []).map(plotHtml).join('');
    root.innerHTML =
      '<div class="gd">'
      + '<div class="gd-bar"><h2>Little Garden</h2>'
      + '<span class="gd-season">' + esc(state.season) + '</span></div>'
      + (state.guest
          ? '<p class="gd-said is-bad">' + esc(HUMAN.login_required) + '</p>'
          : '')
      + yardHtml()
      /* THE SIX CARDS ARE GONE WHEN THERE IS A YARD. Barb: "I don't like that
         the cards are underneath the game. It should be an in game pop up
         when the growing plot is clicked." They were a second drawing of the
         same six beds saying the same six things, and everything they said is
         now said in the popup, over the bed it is about.

         THEY REMAIN AS THE FALLBACK, for a browser holding an old art bundle
         in cache: no yard means no way to reach a bed, and six cards is a
         better answer to that than an empty panel. It should last one
         refresh. */
      + (Yard ? '' : '<ul class="gd-plots">' + plots + '</ul>')
      + '<p class="gd-said" data-said></p>'
      /* THE FRIENDS AND THE MARKETS USED TO BE TWO PANELS BELOW THE GAME.
         Barb: "The friends menu and markets are also below the game... it
         should also open an in game menu with its respective options." They
         are two of the three corner buttons now, and open in the same window
         the bag does. */
      + '<p class="gd-note">Plants grow while they are watered, and one watering '
      + 'lasts about ' + GARDEN.waterHours + ' hours. Leave the garden a while and '
      + 'you lose the growing, never the plant &mdash; nothing here dies.</p>'
      + '</div>';
    placePop();
  }

  async function load() {
    try {
      state = await api('/garden');
      draw();
    } catch (e) {
      /* SAY WHICH THING FAILED. An empty panel that explains nothing is the
         bug the collection page shipped; this one admits it could not ask.

         BUT NOT IN THE CHILD'S FACE. The first version of this printed the
         worker's raw code — a child met the words "not_found" — and then
         told her to refresh, which for a 404 is advice that cannot ever
         work. What she needs is a true sentence she can act on or ignore;
         what the shop needs is the diagnosis, and that goes to the console
         where somebody looking for it will find it. */
      var kind = e.code === 'not_found' ? 'closed'
        : e.code === 'login_required' ? 'guest'
        : 'trouble';
      var says = {
        closed: ['The garden is not open yet.',
          'Nothing is wrong with your plants — this game has not been switched on in the shop.'],
        guest: ['Sign in to start a garden.',
          'Your plots and your seeds are kept with your account.'],
        trouble: ['The garden could not be loaded just now.',
          'Refreshing the page usually sorts it.'],
      }[kind];
      root.innerHTML = '<div class="gd"><p class="gd-said is-bad">' + esc(says[0])
        + '</p><p class="gd-note">' + esc(says[1]) + '</p></div>';
      console.error('[garden] could not load — code=' + (e.code || '?')
        + (e.code === 'not_found'
            ? '. The worker has no /garden route: deploy backend/ (npm run deploy).'
            : ''), e);
    }
  }

  /* PAINT A PART, AND KEEP THE PICTURE HONEST WHILE IT IS IN FLIGHT.
     ------------------------------------------------------------------
     The colour is put on locally first because dragging a slider that waits
     for a server round trip is a slider that judders. The worker is then
     told, and if it refuses, its answer replaces the optimistic picture —
     `act()` refetches the garden on the way through, so a refusal cannot
     leave a colour on screen that is not on the house. */
  var paintPending = null;

  /* REPAINT THE PICTURE WITHOUT REBUILDING THE PANEL.
     ------------------------------------------------------------------
     draw() replaces the whole game, which mid-drag replaces the very slider
     her thumb is on — and a range input that is swapped out underneath a
     drag stops dragging. So while she is moving a slider only the two
     things that show a colour are touched: the house in the yard, and the
     house in the preview. */
  function repaintHouse() {
    var yard = root.querySelector('.yd-art');
    if (yard && Yard) {
      var fresh = Yard.render({ plots: state.plots || [], her: herArt(),
        paint: state.paint || {} }, { label: 'Your garden, outside the house' });
      var holder = document.createElement('div');
      holder.innerHTML = fresh;
      if (holder.firstChild) yard.parentNode.replaceChild(holder.firstChild, yard);
    }
    var pic = root.querySelector('[data-paint-pic]');
    if (pic && Yard && Yard.houseChip) pic.innerHTML = Yard.houseChip(state.paint || {});
    /* The dot on the part's own tab, so the tabs keep up with the house. */
    var tab = root.querySelector('.gd-part[data-part="' + part + '"] i');
    if (tab) tab.style.background = partColour(part);
    /* And which swatch is the current one, if she has landed on one. */
    var now = partColour(part);
    var all = root.querySelectorAll('.gd-swatch');
    for (var i = 0; i < all.length; i++) {
      var on = (all[i].getAttribute('data-hex') || '').toLowerCase() === now;
      all[i].classList.toggle('is-on', on);
      if (on) all[i].setAttribute('aria-current', 'true');
      else all[i].removeAttribute('aria-current');
    }
  }

  function paintNow(hex, live) {
    if (!state.paint) state.paint = {};
    state.paint[part] = String(hex).toLowerCase();
    if (live) repaintHouse(); else draw();
    var body = {};
    body[part] = state.paint[part];
    /* ONE REQUEST PER PAUSE, not one per pixel of slider travel. */
    if (paintPending) clearTimeout(paintPending);
    paintPending = setTimeout(function () {
      paintPending = null;
      /* SAVED WITHOUT A REDRAW, which is not an optimisation.
         ------------------------------------------------------------------
         act() refetches the garden and calls draw(), and draw() rebuilds the
         whole game — including the slider her thumb is still on. The save
         lands a quarter of a second after she stops moving, so the panel
         would be torn down and rebuilt under her hand and the drag would
         end. The picture is already right; there is nothing to redraw.

         NO TOAST EITHER. She can see the house. A line of text saying
         "painted" under a house that just changed colour is a page telling
         her what her own eyes did.

         A REFUSAL STILL SPEAKS, and puts the real house back: load() is the
         only honest answer to "the worker would not have that colour", since
         the optimistic one is on screen and wrong. */
      api('/garden/paint', body).then(function (r) {
        if (r && r.paint) state.paint = r.paint;
      }).catch(function (e) {
        say(humanise(e), 'bad');
        load();
      });
    }, 260);
  }

  /* THE SLIDERS. `input` is every pixel of the drag, which is what makes the
     house follow her thumb; the save is debounced inside paintNow so the
     worker hears about it once she stops. */
  root.addEventListener('input', function (ev) {
    var el = ev.target;
    if (!el || !el.getAttribute || !el.getAttribute('data-mix')) return;
    var group = el.closest('[data-mix-group]');
    if (!group) return;
    var get = function (kind) {
      var one = group.querySelector('[data-mix="' + kind + '"]');
      return one ? Number(one.value) : 0;
    };
    paintNow(hsl2hex(get('h'), get('s'), get('l')), true);
  });

  /* EVERY ACTION IS THE SAME SHAPE: ask the worker, take its answer as the new
     truth, repaint. `busy` is a guard against a double tap spending two seeds
     for one plant — the worker guards that too, with a conditional UPDATE, and
     this only saves her the error message. */
  async function act(fn, good) {
    if (busy) return;
    busy = true;
    try {
      var out = await fn();
      state = await api('/garden');
      draw();
      if (good) say(good(out), 'good');
      /* A FAMILIAR TAGGED HERE CAME BACK WITH SOMETHING. `out.companion` is
         the object the worker sends — Arcade.companion takes that, not a game
         name, which is what the hook that used to sit at the bottom of this
         file got wrong for its whole life. Only picking sets it, and it is
         soft: a toast that fails must never look like a failed harvest. */
      if (out && out.companion && window.Arcade && window.Arcade.companion) {
        try { window.Arcade.companion(out.companion); } catch (e2) { /* not today */ }
      }
    } catch (e) {
      draw();
      say(humanise(e), 'bad');
    } finally { busy = false; }
  }

  /* ---- WHERE A FINGER LANDED, IN TILES ------------------------------- *
   *
   * THE OLD ONE MEASURED TO THE NEAREST BED THROUGH A BOX, and the box was
   * 60 units wide where a tile is 54, with no test for whether the tap was
   * on a bed at all — only which bed was nearest. Sweeping a grid over the
   * lawn afterwards, seventy-two points of plain grass opened a bed menu.
   * Barb met it from the other side: pressing the pavement brought up a bed.
   *
   * So it asks the projection which TILE was pressed, and then what is on
   * that tile. unproject is the same maths that drew the picture, run
   * backwards; there is no second copy of it here and no tolerance to tune.
   */
  function tileAt(svg, ev) {
    var r = svg.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    var vb = (svg.getAttribute('viewBox') || '').split(/\s+/).map(Number);
    if (vb.length !== 4 || !vb[2]) return null;
    var sx = (ev.clientX - r.left) * (vb[2] / r.width);
    var sy = (ev.clientY - r.top) * (vb[3] / r.height);
    var t = Yard.screenToTile(sx, sy, 0);
    return { u: t[0], v: t[1], sx: sx, sy: sy };
  }

  /* A BED IS A ONE-TILE RECTANGLE and the test is whether the point is in
     it. The plants stand well above their soil, so a tap on a tall
     chrysanthemum lands on the grass two tiles behind it — which is why the
     drawing is also asked, in screen space, over the bed's own column only. */
  /* THE DOOR AND THE POSTBOX, which are not tiles. Their boxes come from
     yard-lab, built from the numbers that drew them; the page does not guess
     at where they are. Asked BEFORE the beds, because both stand over ground
     that is walkable and a tap on the door should open the door rather than
     walk her into the wall. */
  function thingUnder(hit) {
    var boxes = Yard.hitBoxes();
    var names = ['door', 'mailbox'];
    for (var i = 0; i < names.length; i++) {
      var b = boxes[names[i]];
      if (hit.sx >= b.x1 && hit.sx <= b.x2 && hit.sy >= b.y1 && hit.sy <= b.y2) {
        return { what: names[i], stand: b.stand };
      }
    }
    return null;
  }

  function bedUnder(hit) {
    var i;
    for (i = 0; i < Yard.PLOT_AT.length; i++) {
      var at = Yard.PLOT_AT[i];
      if (hit.u >= at.u && hit.u <= at.u + 1 && hit.v >= at.v && hit.v <= at.v + 1) return i;
    }
    for (i = 0; i < Yard.PLOT_AT.length; i++) {
      var b = Yard.PLOT_AT[i];
      var p = (state.plots || [])[i];
      if (!p || !p.plant) continue;          // nothing standing up to press
      var c = Yard.tileToScreen(b.u + 0.5, b.v + 0.5, 5);
      if (hit.sx >= c[0] - 16 && hit.sx <= c[0] + 16
        && hit.sy >= c[1] - 48 && hit.sy <= c[1] + 2) return i;
    }
    return -1;
  }

  /* ---- WALKING -------------------------------------------------------- *
   * Barb, twice: "an isometric space my character can walk around in", and
   * then "my avatar is unable to walk."
   *
   * THE GRID IS THE YARD'S OWN TILES. Six of them hold beds — each bed is
   * one tile wide and sits half a tile in, so exactly one integer tile's
   * centre falls inside each — and those are the only obstacles. Outside the
   * fence there is the kerb and the pavement, reachable ONLY through the
   * gate, because a fence you can walk through is a fence nobody believes.
   */
  /* THE RULES LIVE IN yard-lab.mjs, with the geometry they are about — the
     beds, the fence and the gateway are all decided there, and a second
     opinion here about which of them you can stand on would drift from the
     drawing within a month. It also means check-yard.mjs tests them in node
     rather than by clicking. */
  var walkable = function (u, v) { return Yard.walkable(u, v); };

  var here = { u: STAND.u, v: STAND.v };  // where she is standing, in tile centres
  var walking = null;              // the animation in flight, or null

  /* MOVED BY TRANSFORM, NOT BY REDRAWING. The yard is a thousand-odd paths
     whose order is worked out by a topological sort; rebuilding it sixty
     times a second to move one figure would be absurd. Her billboard group
     is nudged instead, and the whole picture is drawn once when she stops so
     that what is in front of what is right again. */
  function moveHerTo(u, v) {
    here.u = u; here.v = v;
    var el = root.querySelector('[data-her]');
    if (!el || !el.parentNode || !el.parentNode.setAttribute) return;
    var c = Yard.tileToScreen(u, v, 0);
    el.parentNode.setAttribute('transform',
      'translate(' + c[0].toFixed(1) + ' ' + c[1].toFixed(1) + ')');
  }

  function walkTo(tile, then) {
    if (!Yard || !look) { if (then) then(); return; }
    var path = Yard.route(here, tile);
    if (!path || path.length < 2) { if (then) then(); return; }
    if (walking) { cancelAnimationFrame(walking.raf); walking = null; }

    /* A STEADY PACE, not a fixed duration: a walk to the far corner should
       take longer than a step next door, or she teleports slowly. */
    var PER_TILE = 190;
    var t0 = null, total = (path.length - 1) * PER_TILE;
    var slow = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (slow) {
      /* NO ANIMATION FOR SOMEBODY WHO HAS ASKED FOR NONE. She arrives. */
      moveHerTo(path[path.length - 1].u + 0.5, path[path.length - 1].v + 0.5);
      draw(); if (then) then(); return;
    }
    walking = { raf: 0 };
    /* A FLAG ON THE BODY while she is moving. The page uses it to ignore a
       second tap mid-stride, and the harness uses it to know when a press
       has finished happening — a tap is no longer followed by a repaint on
       the next tick, it is followed by a walk. */
    try { document.body.dataset.gdWalking = '1'; } catch (e) { /* no body, no flag */ }
    var step = function (now) {
      if (t0 === null) t0 = now;
      var k = Math.min(1, (now - t0) / total);
      var at = k * (path.length - 1);
      var i = Math.min(path.length - 2, Math.floor(at));
      var f = at - i;
      var a = path[i], b = path[i + 1];
      moveHerTo(a.u + 0.5 + (b.u - a.u) * f, a.v + 0.5 + (b.v - a.v) * f);
      if (k < 1) { walking.raf = requestAnimationFrame(step); return; }
      walking = null;
      try { delete document.body.dataset.gdWalking; } catch (e) { /* as above */ }
      /* THE ONE REDRAW, so the fence is in front of her again when she is
         behind it. */
      draw();
      if (then) then();
    };
    walking.raf = requestAnimationFrame(step);
  }

  /* SHE WALKS UP TO A BED, NOT ON TO IT. The nearest tile beside it that she
     can actually stand on, preferring the one she is already closest to. */
  function besideBed(slot) {
    var at = Yard.PLOT_AT[slot];
    var bu = Math.round(at.u), bv = Math.round(at.v);
    var opts = [{ u: bu - 1, v: bv }, { u: bu + 1, v: bv },
      { u: bu, v: bv - 1 }, { u: bu, v: bv + 1 }]
      .filter(function (t) { return walkable(t.u, t.v); });
    if (!opts.length) return null;
    opts.sort(function (x, y) {
      var dx = Math.abs(x.u + 0.5 - here.u) + Math.abs(x.v + 0.5 - here.v);
      var dy = Math.abs(y.u + 0.5 - here.u) + Math.abs(y.v + 0.5 - here.v);
      return dx - dy;
    });
    return opts[0];
  }

  root.addEventListener('click', function (ev) {
    /* THE YARD FIRST, because a tap out there is not on a button at all. */
    /* A TAP INSIDE THE POPUP IS NOT A TAP ON THE YARD, and the popup lives
       inside [data-yard] so that it moves with the picture. Without this
       line, pressing Hoe would be read as a tap on whatever bed happens to
       be under the box — which, since the box sits over its own bed, meant
       every button in it also shut the popup it was in. */
    /* A PRESS ON A BUTTON IS NOT A PRESS ON THE GROUND. The popups and the
       bag both live inside [data-yard] so that they move with the picture,
       which means without this the bag would be read as a tap on the
       pavement underneath it and she would walk off instead of opening it. */
    var inYard = Yard && !ev.target.closest('[data-pop]')
      && !ev.target.closest('button[data-act]')
      && ev.target.closest('[data-yard]');
    if (inYard) {
      if (walking) return;            // she is already on her way somewhere
      var hit = tileAt(inYard.querySelector('.yd-art'), ev);
      if (!hit) return;

      /* THE DOOR AND THE POSTBOX FIRST. Both stand over ground she could walk
         on, and a tap on the door should open the door rather than send her
         walking into the wall. */
      var thing = thingUnder(hit);
      if (thing) {
        if (atThing === thing.what) { atThing = null; draw(); return; }
        atThing = null; open_ = null; choosing = null; panel = null;
        draw();
        walkTo(thing.stand, function () { atThing = thing.what; draw(); });
        return;
      }

      var which = bedUnder(hit);

      if (which < 0) {
        /* NOT A BED: SHE WALKS THERE. Pressing the grass used to open
           whichever bed was nearest, which is how pressing the pavement
           brought up a menu. */
        var tile = { u: Math.floor(hit.u), v: Math.floor(hit.v) };
        if (!walkable(tile.u, tile.v)) return;
        open_ = null; choosing = null; atThing = null; panel = null;
        draw();
        walkTo(tile);
        return;
      }

      /* A BED: SHE WALKS UP TO IT AND THEN THE MENU OPENS. Tapping the one
         that is already open shuts it and she stays where she is. */
      if (open_ === which) { open_ = null; choosing = null; draw(); return; }
      var spot = besideBed(which);
      open_ = null; choosing = null; atThing = null; panel = null;
      draw();
      walkTo(spot || { u: Math.floor(hit.u), v: Math.floor(hit.v) }, function () {
        open_ = which; choosing = null; draw();
      });
      return;
    }

    var b = ev.target.closest('button[data-act]');
    if (!b) return;
    var act_ = b.getAttribute('data-act');
    var slot = Number(b.getAttribute('data-slot'));

    /* TAPPING A BED OPENS ITS MENU, AND TAPPING IT AGAIN SHUTS IT. Only one
       is open at a time: six open menus is six menus, not a garden. */
    if (act_ === 'open') {
      var wasOpen = open_ === slot;
      open_ = wasOpen ? null : slot;
      /* ONE WINDOW AT A TIME, here too. Reaching a bed by keyboard has to put
         the bag down the same way pressing one does, or two windows sit over
         the garden at once and the second one is the one nobody asked for. */
      choosing = null; atThing = null; panel = null;
      draw();
      /* THE KEYBOARD WALKS HER TOO. A bed reached by tab should not be a bed
         she works on from across the garden. */
      if (!wasOpen && Yard) {
        var beside = besideBed(slot);
        if (beside) {
          open_ = null; draw();
          walkTo(beside, function () { open_ = slot; draw(); });
        }
      }
      /* KEEP THE KEYBOARD WHERE IT WAS. draw() replaces the whole panel, so
         the button that was just pressed no longer exists and focus falls
         back to <body> — which sends a keyboard user to the top of the page
         on every tap. The replacement button is put back under the cursor. */
      var again = root.querySelector('.gd-keys button[data-slot="' + slot + '"]');
      if (again && document.activeElement === document.body) again.focus();
      return;
    }
    /* A DIM BUBBLE ANSWERS. It is the only place in this game where a tap is
       meant to fail, and the whole value of it is in the sentence. */
    if (act_ === 'shut') { open_ = null; choosing = null; atThing = null; draw(); return; }

    /* WHICH PART SHE IS PAINTING. A repaint of the panel and nothing else —
       choosing a part is not a change to her house. */
    if (act_ === 'part') { part = b.getAttribute('data-part') || part; draw(); return; }

    /* A SWATCH. The colour goes on at once and the worker is told; if it
       refuses, act() puts the old picture back and says why — the same shape
       every other action in this game has. */
    if (act_ === 'paint') {
      var hex = b.getAttribute('data-hex');
      if (!hex) return;
      paintNow(hex);
      return;
    }
    if (act_ === 'why') { say(b.getAttribute('data-why') || '', 'bad'); return; }
    /* ONE WINDOW AT A TIME. Opening the bag puts down whatever bed or door
       was open, the same way opening those puts down the bag. */
    if (act_ === 'panel' || act_ === 'pack') {
      var want = b.getAttribute('data-panel') || 'bag';
      panel = panel === want ? null : want;
      if (panel) { open_ = null; choosing = null; atThing = null; }
      draw();
      return;
    }
    if (act_ === 'leave') { visiting = null; draw(); return; }

    if (act_ === 'till') {
      act(function () { return api('/garden/till', { slot: slot }); },
        function () { return 'The bed is turned over. Put a seed in it.'; });
      return;
    }

    if (act_ === 'visit') {
      var who = b.getAttribute('data-id');
      act(function () {
        return api('/garden/friend?id=' + encodeURIComponent(who))
          .then(function (g) { visiting = g; return g; });
      }, function (g) { return 'You are in ' + g.handle + '\u2019s garden.'; });
      return;
    }
    if (act_ === 'help') {
      var whose = b.getAttribute('data-id');
      act(function () {
        return api('/garden/help', { friend: whose, slot: slot })
          .then(async function (r) {
            /* THEIR garden is what changed, so re-read THEIRS as well. act()
               refreshes her own, which would otherwise leave the plot she has
               just watered still showing a watering can. */
            visiting = await api('/garden/friend?id=' + encodeURIComponent(whose));
            return r;
          });
      }, function (r) {
        return r.points
          ? 'Watered, and you earned ' + r.points + ' for helping.'
          : 'Watered. You have helped as much as you can today, so this one was free.';
      });
      return;
    }

    if (act_ === 'plant') {
      var key = b.getAttribute('data-plant');
      /* THE BUBBLE AND THE PACKET SHARE A NAME, and they should: both of them
         are "plant". The bubble asks WHICH, the packet answers, and the only
         difference in the markup is whether a plant came with the tap. */
      if (!key) { choosing = slot; open_ = slot; draw(); return; }
      choosing = null;
      act(function () { return api('/garden/plant', { slot: slot, plant: key }); },
        function (r) {
          var def = GARDEN.plants[r.plant];
          return r.inSeason
            ? def.name + ' planted, and it is the right season for it.'
            : def.name + ' planted. It is out of season, so it will take longer.';
        });
      return;
    }
    if (act_ === 'water') {
      act(function () { return api('/garden/water', { slot: slot }); },
        function () { return 'Watered.'; });
      return;
    }
    if (act_ === 'pick') {
      act(function () { return api('/garden/pick', { slot: slot }); },
        function (r) { return r.name + ' picked, and it is in your basket.'; });
      return;
    }
    if (act_ === 'buy') {
      var city = b.getAttribute('data-city');
      var plant = b.getAttribute('data-plant');
      var kind = b.getAttribute('data-kind');
      act(function () { return api('/garden/market', { city: city, plant: plant, kind: kind }); },
        function (r) {
          /* A TOOL IS NOT A PLURAL. "Hoe seeds bought" was what this said
             the first time a tool went through it, because the sentence
             assumed every purchase was something you sow. */
          if (r.kind === 'tool') {
            return r.name + ' bought for ' + r.cost + ' \u2014 it is in your backpack.';
          }
          return r.name + ' ' + (r.kind === 'seed' ? 'seeds' : 'flowers')
            + ' bought for ' + r.cost + '.';
        });
    }
  });

  load();

  /* HER FRIENDS, ASKED FOR ONCE AND SOFTLY. A friends panel that cannot be
     reached must never stop the garden from being drawn — this is the newest
     thing on the page and the least important of them. */
  /* THE AVATAR SCRIPT, AND WHY THIS IS NOT SIMPLY A SCRIPT TAG.
     ------------------------------------------------------------------
     arcade-boot loads arcade-core and arcade-profile; it does not load
     arcade-avatar, so the file that knows how to draw her is not on this
     page. The right fix is one line in the section — and it is there — but
     a section change is exactly what failed to reach the theme this
     afternoon and left the yard invisible for three pushes.

     So this loads it itself when it is missing, and SAYS SO in the console
     when it has to. A workaround that announces itself is a workaround
     somebody can find; a silent one is the bug we just spent an afternoon
     on. If that warning ever appears, the section is stale again. */
  function needAvatar() {
    if (window.Avatar && window.Avatar.full) return Promise.resolve(true);
    var sibling = document.querySelector('script[src*="arcade-garden-art"]');
    if (!sibling) return Promise.resolve(false);
    console.warn('[garden] arcade-avatar.js is not on this page — loading it from '
      + 'game-garden.js instead. The section should carry it; if you see this, '
      + 'sections/arcade-game-garden.liquid on the theme is out of date.');
    return new Promise(function (done) {
      var el = document.createElement('script');
      el.src = sibling.src.replace(/arcade-garden-art\.js/, 'arcade-avatar.js');
      el.onload = function () { done(!!(window.Avatar && window.Avatar.full)); };
      el.onerror = function () { done(false); };
      document.head.appendChild(el);
    });
  }

  (async function () {
    /* HER LOOK comes from /session, which every page in this arcade already
       asks for — the same object the profile page saves. A garden that drew
       a default girl would be drawing somebody else's character. */
    try {
      var ses = await api('/session');
      session = ses;
      var mine = ses && ses.player && ses.player.avatar;
      if (mine && Object.keys(mine).length) {
        var ready = await needAvatar();
        if (ready) {
          look = window.Avatar.clean ? window.Avatar.clean(mine) : mine;
        }
      }
      /* REPAINT WHATEVER HAPPENED. The session carries both her face and the
         postbox's contents, and it lands after the first draw either way. */
      if (state) draw();
    } catch (e) { /* no avatar is a quieter failure than no garden */ }

    try {
      var f = await api('/friends');
      friends = (f && f.friends ? f.friends : [])
        .map(function (x) { return { id: x.id, handle: x.handle }; })
        .filter(function (x) { return x.id && x.handle; });
    } catch (e) { friends = []; }
    if (state) draw();
  }());

  /* NO FAMILIAR HOOK. There was one here and it never did anything:
     Arcade.companion takes the companion OBJECT a worker sends back — every
     other game in this theme passes `res.companion` — and this passed the
     string 'garden', which companionToast() drops on its first line, because a
     string has no `.food`. It read as a feature and was a no-op, which is
     worse than an absence.

     config.js already records the real decision, beside GAMES.garden: "No
     `meets`: a familiar tagging along to the garden is a thing to add later,
     deliberately, not a thing to leave half-wired now." This was the half
     wire. When it is added it goes in the config and rides back on the
     response, the way it does for the room and the sleepover. */
}());
